import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface Agent {
  id: string;
  phoneIdentifier: string | null;
  retellAgentId: string | null;
  defaultForOrganization: boolean | null;
  slackChannelId: string | null;
  facilities: Facility[];
  facilityCount: number;
}

interface Facility {
  id: string;
  name: string | null;
  ehrId: string;
  isPrivateFacility: boolean | null;
  doctorScheduleSyncEnabled: boolean | null;
}

interface BulkActionDialogsProps {
  // Create dialog
  showCreateDialog: boolean;
  setShowCreateDialog: (show: boolean) => void;
  selectedAgent: string;
  setSelectedAgent: (agentId: string) => void;
  selectedFacility: string;
  setSelectedFacility: (facilityId: string) => void;
  agents: Agent[];
  facilities: Facility[];
  onCreateRelationship: () => void;
  isCreating: boolean;

  // Bulk assign dialog
  showBulkAssignDialog: boolean;
  setShowBulkAssignDialog: (show: boolean) => void;
  bulkSelectedAgents: string[];
  setBulkSelectedAgents: (agentIds: string[]) => void;
  bulkSelectedFacilities: string[];
  setBulkSelectedFacilities: (facilityIds: string[]) => void;
  onBulkAssign: () => void;
  isBulkAssigning: boolean;

  // Confirmation dialogs
  confirmDialog: {
    isOpen: boolean;
    agentId: string;
    facilityId: string;
    agentPhone: string | null;
    facilityName: string | null;
  };
  setConfirmDialog: (dialog: {
    isOpen: boolean;
    agentId: string;
    facilityId: string;
    agentPhone: string | null;
    facilityName: string | null;
  }) => void;
  onConfirmSingleDelete: () => void;

  bulkConfirmDialog: {
    isOpen: boolean;
    action: 'assign' | 'remove';
    agentCount: number;
    facilityCount: number;
  };
  setBulkConfirmDialog: (dialog: {
    isOpen: boolean;
    action: 'assign' | 'remove';
    agentCount: number;
    facilityCount: number;
  }) => void;
  onConfirmBulkAction: () => void;
}

export function BulkActionDialogs({
  showCreateDialog,
  setShowCreateDialog,
  selectedAgent,
  setSelectedAgent,
  selectedFacility,
  setSelectedFacility,
  agents,
  facilities,
  onCreateRelationship,
  isCreating,
  showBulkAssignDialog,
  setShowBulkAssignDialog,
  bulkSelectedAgents,
  setBulkSelectedAgents,
  bulkSelectedFacilities,
  setBulkSelectedFacilities,
  onBulkAssign,
  isBulkAssigning,
  confirmDialog,
  setConfirmDialog,
  onConfirmSingleDelete,
  bulkConfirmDialog,
  setBulkConfirmDialog,
  onConfirmBulkAction,
}: BulkActionDialogsProps) {
  return (
    <>
      {/* Create Single Relationship Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Agent-Facility Relationship</DialogTitle>
            <DialogDescription>
              Create a new relationship between an agent and a facility.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Agent</Label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents?.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.phoneIdentifier || 'No phone'}{' '}
                      {agent.defaultForOrganization && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Facility</Label>
              <Select value={selectedFacility} onValueChange={setSelectedFacility}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a facility" />
                </SelectTrigger>
                <SelectContent>
                  {facilities?.map((facility) => (
                    <SelectItem key={facility.id} value={facility.id}>
                      {facility.name || `EHR ID: ${facility.ehrId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={onCreateRelationship} disabled={isCreating}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Relationship
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Assign Facilities to Agents</DialogTitle>
            <DialogDescription>
              Select multiple agents and facilities to create relationships in bulk
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label>Select Agents</Label>
              <div className="border rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
                {agents?.map((agent) => (
                  <div key={agent.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={bulkSelectedAgents.includes(agent.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setBulkSelectedAgents([...bulkSelectedAgents, agent.id]);
                        } else {
                          setBulkSelectedAgents(bulkSelectedAgents.filter((id) => id !== agent.id));
                        }
                      }}
                    />
                    <Label className="text-sm font-normal">
                      {agent.phoneIdentifier || 'No phone'}
                      {agent.defaultForOrganization && ' (Default)'}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <Label>Select Facilities</Label>
              <div className="border rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
                {facilities?.map((facility) => (
                  <div key={facility.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={bulkSelectedFacilities.includes(facility.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setBulkSelectedFacilities([...bulkSelectedFacilities, facility.id]);
                        } else {
                          setBulkSelectedFacilities(
                            bulkSelectedFacilities.filter((id) => id !== facility.id)
                          );
                        }
                      }}
                    />
                    <Label className="text-sm font-normal">
                      {facility.name || `EHR ID: ${facility.ehrId}`}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={onBulkAssign} disabled={isBulkAssigning}>
              {isBulkAssigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign ({bulkSelectedAgents.length} agents, {bulkSelectedFacilities.length}{' '}
              facilities)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, isOpen: open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Agent-Facility Relationship</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the relationship between agent "
              {confirmDialog.agentPhone || 'No phone'}" and facility "
              {confirmDialog.facilityName || 'Unnamed Facility'}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmSingleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Relationship
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Action Confirmation Dialog */}
      <AlertDialog
        open={bulkConfirmDialog.isOpen}
        onOpenChange={(open) => setBulkConfirmDialog({ ...bulkConfirmDialog, isOpen: open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkConfirmDialog.action === 'assign'
                ? 'Bulk Assign Facilities'
                : 'Bulk Remove Relationships'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkConfirmDialog.action === 'assign'
                ? `Are you sure you want to assign ${bulkConfirmDialog.facilityCount} facilities to ${bulkConfirmDialog.agentCount} agents? This will create multiple new relationships.`
                : 'Are you sure you want to remove the selected relationships? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmBulkAction}
              className={
                bulkConfirmDialog.action === 'remove'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
            >
              {bulkConfirmDialog.action === 'assign' ? 'Assign Facilities' : 'Remove Relationships'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
